import { applicationCapabilities, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import { IconVariants } from "@next-platform/client-common-spicy-ui";

import { Collection } from "@/types/api";
import { HitDropdownActions } from "@/types/hits";

import { CharacterCardDropdownActions } from "../types";

export interface IGetDropdownActionsProps {
  t: (key: string) => string;
  characterCardActionsHandler: (action: CharacterCardDropdownActions, characterId: string) => void;
  isAuthenticated: boolean;
  id: string;
  collection?: Collection;
  /** Rulebook only: the index says the definition is public, so a logged-in user may clone it. */
  canClone?: boolean;
  /** Rulebook only: the viewer has a rulebook attach capability, so the menu leads with Attach. */
  canAttach?: boolean;
}

const resolveShareCapability = (collection: Collection): SubCapabilityKey => {
  if (collection === Collection.LOREBOOKS) {
    return SubCapabilityKey.LorebookShareLink;
  }
  if (collection === Collection.RULEBOOKS) {
    return SubCapabilityKey.RulebookPublicSharing;
  }
  return SubCapabilityKey.CharacterShareLink;
};

export default function getDropdownActions({
  t,
  collection = Collection.CHARACTERS,
  characterCardActionsHandler,
  isAuthenticated,
  id,
  canClone = false,
  canAttach = false,
}: IGetDropdownActionsProps): HitDropdownActions[] {
  // Rulebooks have no dedicated share-link capability — sharing is part of public discovery.
  const shareCapability = resolveShareCapability(collection);
  const isShareAllowed = applicationCapabilities.isAllowed(shareCapability);
  const result = [
    ...(collection === Collection.RULEBOOKS && canAttach
      ? [
          {
            label: t("rulebook:card.actions.attach"),
            icon: IconVariants.LINK,
            onPress: () => characterCardActionsHandler(CharacterCardDropdownActions.ATTACH, id),
            isHidden: !isAuthenticated,
          },
        ]
      : []),
    ...(isShareAllowed
      ? [
          {
            label: t("chatbotCard:dropdown.share"),
            icon: IconVariants.SHARE,
            onPress: () => characterCardActionsHandler(CharacterCardDropdownActions.SHARE, id),
          },
        ]
      : []),
    ...(collection === Collection.RULEBOOKS && canClone
      ? [
          {
            label: t("rulebook:explore.clone"),
            icon: IconVariants.COPY,
            onPress: () => characterCardActionsHandler(CharacterCardDropdownActions.CLONE, id),
            isHidden: !isAuthenticated,
          },
        ]
      : []),
    {
      label: t("chatbotCard:dropdown.report"),
      icon: IconVariants.FLAG,
      onPress: () => characterCardActionsHandler(CharacterCardDropdownActions.REPORT, id),
    },
    {
      label: t("chatbotCard:dropdown.blockCreator"),
      icon: IconVariants.USER_CANCEL,
      onPress: () => characterCardActionsHandler(CharacterCardDropdownActions.BLOCK, id),
      isHidden: !isAuthenticated,
    },
  ];

  // Less-like-this is a characters-only recommendation signal — no backend for lorebooks or rulebooks.
  if (collection === Collection.CHARACTERS) {
    result.push({
      label: t("chatbotCard:dropdown.lessLikeThis"),
      icon: IconVariants.CIRCLE_MINUS,
      onPress: () => characterCardActionsHandler(CharacterCardDropdownActions.LESS_LIKE_THIS, id),
      isHidden: !isAuthenticated,
    });
  }

  return result;
}
